import { useState } from 'react';
import { BarChart3, BookOpen, ChevronRight, Edit3, Trash2, PlusCircle, Clock, Sliders, Layers, Link2, Target, ArrowUpDown, Zap, X, Plus, Pin } from 'lucide-react';
import { defaultSetupTemplates, getConfluencesByCategory } from '../../utils/constants';
import { fmtUSD, safePnl } from '../../utils/formatters';
import { futuresSpecs, getSpec } from '../../config/futuresSpecs';
import { calcTradeStats, calcRStats } from '../../services/statsCalculator';
import { coach, filterMuted, enrichWithMuteAction } from '../../services/coach';
import { getCurrentTradingDay } from '../../services/tradeProcessor';
import CoachObservationsPanel from '../Coach/CoachObservationsPanel';
import SlicerBar from '../Slicer/SlicerBar';
import GlassSelect from '../Common/GlassSelect';

const SetupsTab = ({
  darkMode,
  filteredTrades,
  closedFilteredTrades,
  setupAtmBaseTrades,
  setupDetails,
  atmDetails = {},                  // ATM template configs — passed to coach for cross-tab analysis
  allJournalEntries = {},           // For news/event sensitivity analysis (future)
  mistakes = [],                    // Mistakes library — for cross-tab mistake-by-setup analysis
  getTradeNetPnL,                   // P&L attribution helper
  getAccountById,                   // Account resolution helper
  onCoachAction,                    // Dispatcher for structured Coach actions (Pin, etc.)
  mutedObservations = {},           // Map of observationId → ISO expiresAt
  confluences,
  setConfluenceCombos,
  excludeMistakesFromSetupStats,
  setExcludeMistakesFromSetupStats,
  expandedSetupAnalysis,
  setupsViewMode,
  setSetupsViewMode,
  setExpandedSetupAnalysis,
  // Setup analytics (from useSetupAnalytics)
  getSetupStats,
  getHourlyStatsForTrades,
  getAtmPerformanceForSetup,
  getConfluencePerformanceForSetup,
  getComboPerformanceForSetup,
  getConfluencePerformanceForSetupAndAtm,
  getRDistributionForTrades,
  getStopLossBreakdownForSetup,
  getDirectionSplitForSetup,
  getCounterConfluenceImpactForSetup,
  // Setup CRUD callbacks
  getAllSetupsWithDetails,
  updateSetupDescription,
  addSetupRule,
  removeSetupRule,
  updateSetupRule,
  toggleSetupConfluence,
  toggleSetupImpliedConfluence,
  // Phase 1A Slicer — state + actions owned by the parent so analytics are
  // pre-sliced before they reach the analytics hook
  slice = null,
  sliceActions = null,
  confluenceLookup = {},
  unfilteredSetupAtmBaseTrades = null,  // un-sliced version, used as the slicer's "100%" reference for "X of Y trades"
  // Cross-tab navigation
  onNavigateToAtm,
}) => {
  // Local state (view-only, not persisted)

  const [setupsSortBy, setSetupsSortBy] = useState('totalPnL');
  const [setupsMinTrades, setSetupsMinTrades] = useState(0);
  const [selectedSetupId, setSelectedSetupId] = useState(null);
  const [editingSetupId, setEditingSetupId] = useState(null);
  const [newRuleText, setNewRuleText] = useState('');
  const [creatingComboForSetup, setCreatingComboForSetup] = useState(null);
  const [newComboName, setNewComboName] = useState('');
  const [newComboIds, setNewComboIds] = useState([]);

  return (
          <div className={`rounded-lg shadow-md p-6 transition-colors duration-300 ${darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/30 backdrop-blur-md border border-white/30'}`}>
            {/* Header with View Toggle */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h2 className={`text-xl font-bold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Trading Setups
                </h2>
                <p className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {setupsViewMode === 'library'
                    ? 'Document your setups with rules and descriptions'
                    : 'Analyze performance across all setups'}
                </p>
              </div>
              <div className="flex items-center gap-4">
                {/* Clean Metrics Toggle */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setExcludeMistakesFromSetupStats(prev => !prev)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      excludeMistakesFromSetupStats
                        ? 'bg-emerald-500'
                        : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                    }`}
                    title={excludeMistakesFromSetupStats ? 'Showing clean metrics (mistakes excluded)' : 'Showing all trades including mistakes'}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      excludeMistakesFromSetupStats ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <span className={`text-xs whitespace-nowrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Clean Metrics
                  </span>
                </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setSetupsViewMode('analysis')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    setupsViewMode === 'analysis'
                      ? 'bg-blue-500 text-white'
                      : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <BarChart3 size={18} />
                  Analysis
                </button>
                <button
                  onClick={() => setSetupsViewMode('library')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    setupsViewMode === 'library'
                      ? 'bg-blue-500 text-white'
                      : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <BookOpen size={18} />
                  Library
                </button>
              </div>
              </div>
            </div>

            {/* Setup Library View */}
            {setupsViewMode === 'library' && (
              <div className="space-y-4">
                {getAllSetupsWithDetails().map((setup) => {
                  const stats = getSetupStats(setup.name);
                  const isExpanded = selectedSetupId === setup.id;
                  const isEditing = editingSetupId === setup.id;
                  
                  return (
                    <div 
                      key={setup.id}
                      className={`rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                        darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'
                      } ${isExpanded ? 'ring-2 ring-blue-500' : ''}`}
                    >
                      {/* Setup Header - Always visible */}
                      <div 
                        className={`p-4 cursor-pointer ${isExpanded ? '' : 'hover:bg-white/5'}`}
                        onClick={() => setSelectedSetupId(isExpanded ? null : setup.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${setup.isDefault ? 'bg-blue-500' : 'bg-purple-500'}`} />
                            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {setup.name}
                            </h3>
                            {!setup.isDefault && (
                              <span className={`text-xs px-2 py-0.5 rounded-sm ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                                Custom
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4">
                            {stats && (
                              <div className="flex items-center gap-4 text-sm">
                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                                  {stats.trades} trades
                                </span>
                                <span className={`font-medium ${parseFloat(stats.winRate) >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                                  {stats.winRate}% WR
                                </span>
                                <span className={`font-bold ${stats.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  {stats.totalPnL >= 0 ? '+' : '-'}${fmtUSD(stats.totalPnL)}
                                </span>
                                {excludeMistakesFromSetupStats && (() => {
                                  const excludedCount = closedFilteredTrades.filter(
                                    t => t.setup === setup.name && t.mistakes?.length > 0
                                  ).length;
                                  return excludedCount > 0 ? (
                                    <span className="text-xs text-amber-400/70">
                                      ✦ {excludedCount} excluded
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                            )}
                            <ChevronRight
                              size={20}
                              className={`transition-transform duration-200 ${darkMode ? 'text-gray-400' : 'text-gray-500'} ${isExpanded ? 'rotate-90' : ''}`}
                            />
                          </div>
                        </div>
                        {/* Description preview when collapsed */}
                        {!isExpanded && setup.description && (
                          <p className={`mt-2 text-sm truncate ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {setup.description}
                          </p>
                        )}
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className={`px-4 pb-4 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                          {/* Edit/View Toggle */}
                          <div className="flex justify-end mt-3 mb-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingSetupId(isEditing ? null : setup.id);
                              }}
                              className={`flex items-center gap-1 px-3 py-1 rounded text-sm ${
                                isEditing 
                                  ? 'bg-green-500 text-white' 
                                  : darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              <Edit3 size={14} />
                              {isEditing ? 'Done Editing' : 'Edit Setup'}
                            </button>
                          </div>

                          {/* Description Section */}
                          <div className="mb-4">
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Description
                            </label>
                            {isEditing ? (
                              <textarea
                                value={setupDetails[setup.name]?.description || setup.description || ''}
                                onChange={(e) => updateSetupDescription(setup.name, e.target.value)}
                                placeholder="Describe when and how to use this setup..."
                                rows={3}
                                className="w-full input-modern"
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <p className={`p-3 rounded-lg ${darkMode ? 'bg-gray-600 text-gray-200' : 'bg-white text-gray-700'}`}>
                                {setupDetails[setup.name]?.description || setup.description || 'No description added yet.'}
                              </p>
                            )}
                          </div>

                          {/* Rules Section */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Setup Rules ({(setupDetails[setup.name]?.rules || setup.rules || []).length})
                            </label>
                            <div className={`rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'} p-3`}>
                              {(setupDetails[setup.name]?.rules || setup.rules || []).length === 0 ? (
                                <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  No rules defined yet. {isEditing ? 'Add rules below.' : 'Click "Edit Setup" to add rules.'}
                                </p>
                              ) : (
                                <ul className="space-y-2">
                                  {(setupDetails[setup.name]?.rules || setup.rules || []).map((rule, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                      <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                        darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                                      }`}>
                                        {idx + 1}
                                      </span>
                                      {isEditing ? (
                                        <div className="flex-1 flex items-center gap-2">
                                          <input
                                            type="text"
                                            value={rule}
                                            onChange={(e) => updateSetupRule(setup.name, idx, e.target.value)}
                                            className="flex-1 input-modern"
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              removeSetupRule(setup.name, idx);
                                            }}
                                            className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-sm"
                                          >
                                            <Trash2 size={16} />
                                          </button>
                                        </div>
                                      ) : (
                                        <span className={`${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{rule}</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              
                              {/* Add New Rule */}
                              {isEditing && (
                                <div className="mt-3 pt-3 border-t border-gray-500/30">
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={newRuleText}
                                      onChange={(e) => setNewRuleText(e.target.value)}
                                      placeholder="Add a new rule..."
                                      className="flex-1 input-modern"
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          addSetupRule(setup.name, newRuleText);
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addSetupRule(setup.name, newRuleText);
                                      }}
                                      className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-sm hover:bg-blue-600"
                                    >
                                      <PlusCircle size={16} />
                                      Add
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Recommended Confluences Section */}
                          <div className="mt-4">
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Recommended Confluences ({setup.recommendedConfluences?.length || 0})
                            </label>
                            <div className={`rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'} p-3`}>
                              {isEditing ? (
                                /* Editing Mode - Show all confluences grouped by category */
                                <div className="space-y-3">
                                  {Object.entries(getConfluencesByCategory(confluences)).map(([category, categoryConfluences]) => (
                                    <div key={category}>
                                      <div className={`text-xs font-medium mb-1 text-left ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                                        {category}
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {categoryConfluences.map(conf => {
                                          const isSelected = setup.recommendedConfluences?.includes(conf.id);
                                          const defaultTemplate = defaultSetupTemplates.find(t => t.name === setup.name);
                                          const defaultConfluences = defaultTemplate?.recommendedConfluences || [];
                                          
                                          return (
                                            <button
                                              key={conf.id}
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                toggleSetupConfluence(setup.name, conf.id, defaultConfluences);
                                              }}
                                              className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${
                                                isSelected
                                                  ? 'bg-purple-500 text-white'
                                                  : darkMode
                                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-500'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                              }`}
                                            >
                                              {conf.name}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                /* View Mode - Show only selected confluences */
                                setup.recommendedConfluences?.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {setup.recommendedConfluences.map(confId => {
                                      const conf = confluences.find(c => c.id === confId);
                                      if (!conf) return null;
                                      return (
                                        <span
                                          key={confId}
                                          className={`px-3 py-1 rounded-full text-sm ${
                                            darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                                          }`}
                                        >
                                          {conf.name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    No recommended confluences set. Click “Edit Setup” to add them.
                                  </p>
                                )
                              )}
                            </div>
                          </div>

                          {/* Implied Confluences Section (Wave E.1) — chips here
                              are auto-tagged on every trade with this setup. Distinct
                              from "Recommended" above: recommended is advisory
                              ("Coach warns if missing"); implied is definitional
                              ("setup name encodes this confluence"). Default empty —
                              the user opts in per-setup. */}
                          <div className="mt-4">
                            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Implied Confluences ({setup.impliedConfluences?.length || 0})
                              <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                — auto-tagged on every trade with this setup
                              </span>
                            </label>
                            <div className={`rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white'} p-3`}>
                              {isEditing ? (
                                <div className="space-y-3">
                                  {Object.entries(getConfluencesByCategory(confluences)).map(([category, categoryConfluences]) => (
                                    <div key={category}>
                                      <div className={`text-xs font-medium mb-1 text-left ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                                        {category}
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {categoryConfluences.map(conf => {
                                          const isSelected = setup.impliedConfluences?.includes(conf.id);
                                          return (
                                            <button
                                              key={conf.id}
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (typeof toggleSetupImpliedConfluence === 'function') {
                                                  toggleSetupImpliedConfluence(setup.name, conf.id);
                                                }
                                              }}
                                              className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${
                                                isSelected
                                                  ? 'bg-emerald-500 text-white'
                                                  : darkMode
                                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-500'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                              }`}
                                              title={isSelected
                                                ? `Implied: every trade with this setup auto-tags "${conf.name}"`
                                                : `Click to auto-tag "${conf.name}" on every trade with this setup`}
                                            >
                                              {conf.name}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                setup.impliedConfluences?.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {setup.impliedConfluences.map(confId => {
                                      const conf = confluences.find(c => c.id === confId);
                                      if (!conf) return null;
                                      return (
                                        <span
                                          key={confId}
                                          className={`px-3 py-1 rounded-full text-sm ${
                                            darkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                                          }`}
                                          title={`Auto-tagged on every trade with this setup`}
                                        >
                                          {conf.name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    No implied confluences set. Click &ldquo;Edit Setup&rdquo; to pin chips that should auto-tag on every trade with this setup (e.g., the &ldquo;15M 21EMA&rdquo; chip for a setup named after it).
                                  </p>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Setup Analysis View (existing) */}
            {setupsViewMode === 'analysis' && (
              <>
                {filteredTrades.length === 0 ? (
                  <div className={`text-center py-8 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No trades found. Add some trades to see setup analytics!
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Sort and Filter Dropdowns */}
                    <div className="flex items-center justify-start gap-4">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sort by:</span>
                        <GlassSelect
                          value={setupsSortBy}
                          onChange={(v) => setSetupsSortBy(v)}
                          options={[
                            { value: 'totalPnL', label: 'Total P&L' },
                            { value: 'winRate', label: 'Win Rate' },
                            { value: 'avgPnL', label: 'Avg P&L' },
                            { value: 'profitFactor', label: 'Profit Factor' },
                            { value: 'avgR', label: 'Avg R' },
                            { value: 'trades', label: 'Trade Count' },
                          ]}
                          darkMode={darkMode}
                          wrapperClassName="relative inline-block"
                          className="w-40"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Min trades:</span>
                        <GlassSelect
                          value={setupsMinTrades}
                          onChange={(v) => setSetupsMinTrades(Number(v))}
                          options={[
                            { value: 0, label: 'All' },
                            { value: 5, label: '5+' },
                            { value: 10, label: '10+' },
                            { value: 20, label: '20+' },
                            { value: 50, label: '50+' },
                          ]}
                          darkMode={darkMode}
                          wrapperClassName="relative inline-block"
                          className="w-24"
                        />
                      </div>
                    </div>
                    {(() => {
                      // Calculate stats for each setup (closed trades only — open trades have no P&L)
                      const setupStats = {};

                  setupAtmBaseTrades.forEach(trade => {
                    const setup = trade.setup || 'Unknown';
                    if (!setupStats[setup]) {
                      setupStats[setup] = {
                        trades: [],
                        totalPnL: 0,
                        wins: 0,
                        losses: 0,
                        totalR: 0
                      };
                    }
                    setupStats[setup].trades.push(trade);
                    setupStats[setup].totalPnL += safePnl(trade);
                    setupStats[setup].totalR += parseFloat(trade.rMultiple) || 0;
                    if (safePnl(trade) > 0) setupStats[setup].wins++;
                    else if (safePnl(trade) < 0) setupStats[setup].losses++;
                  });
                  
                  // Sort setups by total P&L
                  const sortedSetups = Object.entries(setupStats)
                    .map(([name, stats]) => {
                      const coreStats = calcTradeStats(stats.trades);
                      const rStats = calcRStats(stats.trades);
                      const { totalTrades, winRate, totalPnL: _corePnL, avgPnL, avgWin, avgLoss, profitFactor } = coreStats;
                      const avgR = rStats.avgR;

                      // Wick Over: only count trades where field is filled in; convert points→ticks for consistency
                      const tradesWithWickOver = stats.trades.filter(t => t.wickOver !== undefined && t.wickOver !== '' && parseFloat(t.wickOver) >= 0 && t.wickOver !== null);
                      const avgWickOver = tradesWithWickOver.length > 0
                        ? tradesWithWickOver.reduce((sum, t) => {
                            let val = parseFloat(t.wickOver);
                            // Convert points to ticks if stored in points
                            if (t.useWickOverTicks === false) {
                              const spec = getSpec(t.symbol);
                              if (spec) val = val / spec.tickSize;
                            }
                            return sum + val;
                          }, 0) / tradesWithWickOver.length
                        : null;

                      const bestTrade = Math.max(...stats.trades.map(t => safePnl(t)));
                      const worstTrade = Math.min(...stats.trades.map(t => safePnl(t)));

                      return {
                        name,
                        totalTrades,
                        wins: coreStats.wins,
                        losses: coreStats.losses,
                        winRate,
                        totalPnL: coreStats.totalPnL,
                        avgPnL,
                        avgR,
                        avgWickOver,
                        wickOverCount: tradesWithWickOver.length,
                        avgWin,
                        avgLoss,
                        profitFactor,
                        bestTrade,
                        worstTrade
                      };
                    })
                    .filter(setup => setup.totalTrades >= setupsMinTrades)
                    .sort((a, b) => {
                      // Dynamic sorting based on selected option
                      switch (setupsSortBy) {
                        case 'winRate':
                          return b.winRate - a.winRate;
                        case 'avgPnL':
                          return b.avgPnL - a.avgPnL;
                        case 'profitFactor':
                          // Handle Infinity cases
                          if (a.profitFactor === Infinity && b.profitFactor === Infinity) return 0;
                          if (a.profitFactor === Infinity) return -1;
                          if (b.profitFactor === Infinity) return 1;
                          return b.profitFactor - a.profitFactor;
                        case 'avgR':
                          return b.avgR - a.avgR;
                        case 'trades':
                          return b.totalTrades - a.totalTrades;
                        case 'totalPnL':
                        default:
                          return b.totalPnL - a.totalPnL;
                      }
                    });
                  
                  return sortedSetups.map((setup, index) => {
                    const isExpanded = expandedSetupAnalysis === setup.name;
                    const setupTrades = setupAtmBaseTrades.filter(t => t.setup === setup.name);
                    const hourlyStats = isExpanded ? getHourlyStatsForTrades(setupTrades) : [];
                    const atmPerformance = isExpanded ? getAtmPerformanceForSetup(setup.name) : [];
                    // Coach observations for this setup — only computed when
                    // the card is expanded so closed cards don't pay the cost.
                    const setupObservations = isExpanded
                      ? enrichWithMuteAction(
                          filterMuted(
                            coach.analyze(
                              { type: 'setup', id: setup.name },
                              {
                                allTrades: setupAtmBaseTrades,
                                anchorDate: getCurrentTradingDay(),
                                // Cross-tab context — analyzers can ask "what's
                                // pinned today?", "which ATM was used?", "any
                                // event around this date?" without per-call plumbing.
                                allConfluences: confluences || [],
                                allMistakes: mistakes,
                                atmDetails,
                                setupDetails,
                                allJournalEntries,
                                getTradeNetPnL,
                                getAccountById,
                              }
                            ),
                            mutedObservations
                          )
                        )
                      : [];
                    
                    return (
                    <div 
                      key={setup.name}
                      id={`setup-card-${setup.name.replace(/\s+/g, '-')}`}
                      className={`rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                        darkMode ? 'bg-black/45 backdrop-blur-md border border-white/10' : 'bg-white/20 backdrop-blur-xs border border-white/20'
                      } ${isExpanded ? 'ring-2 ring-blue-500' : ''}`}
                    >
                      {/* Clickable Header */}
                      <div
                        className={`p-4 cursor-pointer hover:opacity-90 transition-opacity`}
                        onClick={() => setExpandedSetupAnalysis(isExpanded ? null : setup.name)}
                      >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`text-lg font-bold px-2 py-1 rounded ${
                            index === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                            index === 1 ? 'bg-gray-400/20 text-gray-400' :
                            index === 2 ? 'bg-orange-500/20 text-orange-500' :
                            darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                          }`}>
                            #{index + 1}
                          </span>
                          <h3 className={`text-lg font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {setup.name}
                          </h3>
                          <ChevronRight
                            size={20}
                            className={`transition-transform duration-200 ${darkMode ? 'text-gray-400' : 'text-gray-500'} ${isExpanded ? 'rotate-90' : ''}`}
                          />
                          {excludeMistakesFromSetupStats && (() => {
                            const excludedCount = closedFilteredTrades.filter(
                              t => t.setup === setup.name && t.mistakes?.length > 0
                            ).length;
                            return excludedCount > 0 ? (
                              <span className="text-xs text-amber-400/70 ml-1">
                                ✦ {excludedCount} mistake{excludedCount !== 1 ? 's' : ''} excluded
                              </span>
                            ) : null;
                          })()}
                        </div>
                        <div className={`text-xl font-bold ${setup.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {setup.totalPnL >= 0 ? '+' : '-'}${fmtUSD(setup.totalPnL)}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Trades
                          </div>
                          <div className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {setup.totalTrades}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Win Rate
                          </div>
                          <div className={`font-semibold ${setup.winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                            {setup.winRate.toFixed(1)}%
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg P&L
                          </div>
                          <div className={`font-semibold ${setup.avgPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {setup.avgPnL >= 0 ? '+' : '-'}${fmtUSD(setup.avgPnL)}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg R
                          </div>
                          <div className={`font-semibold ${setup.avgR >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {setup.avgR >= 0 ? '+' : ''}{setup.avgR.toFixed(2)}R
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Profit Factor
                          </div>
                          <div className={`font-semibold text-sm ${setup.profitFactor === Infinity ? (darkMode ? 'text-gray-400' : 'text-gray-500') : setup.profitFactor >= 1 ? 'text-green-500' : 'text-red-500'}`}>
                            {setup.profitFactor === Infinity ? 'Not Enough Data' : setup.profitFactor.toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            W/L
                          </div>
                          <div className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            <span className="text-green-500">{setup.wins}</span>
                            <span className={`mx-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>/</span>
                            <span className="text-red-500">{setup.losses}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className={`mt-3 pt-3 border-t grid grid-cols-2 md:grid-cols-4 gap-4 transition-colors duration-300 ${
                        darkMode ? 'border-gray-600' : 'border-gray-200'
                      }`}>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg Winner
                          </div>
                          <div className="font-semibold text-green-500">
                            +${fmtUSD(setup.avgWin)}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Avg Loser
                          </div>
                          <div className="font-semibold text-red-500">
                            -${fmtUSD(setup.avgLoss)}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Best Trade
                          </div>
                          <div className="font-semibold text-green-500">
                            +${fmtUSD(setup.bestTrade)}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Worst Trade
                          </div>
                          <div className={`font-semibold ${setup.worstTrade >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {setup.worstTrade >= 0 ? '+' : '-'}${fmtUSD(setup.worstTrade)}
                          </div>
                        </div>
                        {setup.avgWickOver !== null && (
                          <div>
                            <div className={`text-xs uppercase tracking-wide mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Avg Wick Over
                            </div>
                            <div className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {setup.avgWickOver.toFixed(1)}t
                              <span className={`text-xs font-normal ml-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                ({setup.wickOverCount} trade{setup.wickOverCount !== 1 ? 's' : ''})
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      </div>

                      {/* Expanded Section - Coach Observations + Time of Day & ATM Breakdown */}
                      {isExpanded && (
                        <div className={`border-t px-4 pb-4 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                          {/* Phase 1A Slicer — chip-based multi-dimensional filter.
                              All analytics below this point automatically reflect the
                              slice because the parent pre-filters setupAtmBaseTrades
                              for this expanded setup before useSetupAnalytics sees
                              them. Renders only when slice state was wired through
                              (graceful degrade — older parents that don't pass slice
                              props just skip the bar). */}
                          {slice && sliceActions && unfilteredSetupAtmBaseTrades && (
                            <div className="mt-4">
                              <SlicerBar
                                baseTrades={unfilteredSetupAtmBaseTrades.filter(t => t.setup === setup.name)}
                                confluenceLookup={confluenceLookup}
                                slice={slice}
                                setDimension={sliceActions.setDimension}
                                setRange={sliceActions.setRange}
                                clearDimension={sliceActions.clearDimension}
                                clearAll={sliceActions.clearAll}
                                darkMode={darkMode}
                              />
                            </div>
                          )}

                          {/* AI Coach observations for this setup. Reads
                              `trade.barAnalysis` populated via the chart load
                              path or Settings → Charts → Run backfill. */}
                          <div className="mt-4">
                            <CoachObservationsPanel
                              observations={setupObservations}
                              title="AI Coach"
                              emptyMessage={`Coach needs more analyzed trades for "${setup.name}". Open recent trades' charts (or run Settings → Charts → backfill) to populate bar analysis.`}
                              darkMode={darkMode}
                              onAction={onCoachAction}
                            />
                          </div>

                          {/* Time of Day Performance */}
                          <div className="mt-4">
                            <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              <Clock size={16} className="text-blue-500" />
                              Performance by Hour
                            </h4>
                            {hourlyStats.length === 0 ? (
                              <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                No time data available for this setup
                              </p>
                            ) : (
                              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                {hourlyStats.map(h => {
                                  const maxPnL = Math.max(...hourlyStats.map(x => Math.abs(x.totalPnL)));
                                  const intensity = maxPnL > 0 ? Math.abs(h.totalPnL) / maxPnL : 0;
                                  
                                  return (
                                    <div 
                                      key={h.hour}
                                      className={`p-2 rounded-lg text-center transition-colors ${
                                        h.totalPnL > 0 
                                          ? `bg-green-500/${Math.round(20 + intensity * 30)}` 
                                          : h.totalPnL < 0 
                                            ? `bg-red-500/${Math.round(20 + intensity * 30)}`
                                            : darkMode ? 'bg-gray-700' : 'bg-gray-200'
                                      }`}
                                      style={{
                                        backgroundColor: h.totalPnL > 0 
                                          ? `rgba(34, 197, 94, ${0.15 + intensity * 0.35})` 
                                          : h.totalPnL < 0 
                                            ? `rgba(239, 68, 68, ${0.15 + intensity * 0.35})`
                                            : undefined
                                      }}
                                    >
                                      <div className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {h.hour.toString().padStart(2, '0')}:00
                                      </div>
                                      <div className={`text-sm font-bold ${h.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                        {h.totalPnL >= 0 ? '+' : '-'}${Math.abs(h.totalPnL).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                      </div>
                                      {h.rCount > 0 && (
                                        <div className={`text-xs font-semibold ${h.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                                          {h.avgR >= 0 ? '+' : ''}{h.avgR.toFixed(1)}R
                                        </div>
                                      )}
                                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {h.trades} ({h.winRate.toFixed(0)}%)
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Best ATMs for this Setup */}
                          <div className="mt-4">
                            <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              <Sliders size={16} className="text-purple-500" />
                              ATM Performance with {setup.name}
                            </h4>
                            {atmPerformance.length === 0 ? (
                              <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                No ATM data available for this setup
                              </p>
                            ) : (
                              <div className="grid gap-2">
                                {atmPerformance.slice(0, 5).map((atm, idx) => {
                                  const atmConfluences = getConfluencePerformanceForSetupAndAtm(setup.name, atm.name);
                                  return (
                                  <div
                                    key={atm.name}
onClick={() => onNavigateToAtm(atm.name)}
                                    className={`p-2 rounded-lg grid grid-cols-3 items-center cursor-pointer transition-colors duration-150 ${
                                      darkMode ? 'bg-gray-700/50 hover:bg-gray-600/60' : 'bg-white hover:bg-gray-50'
                                    }`}
                                    title={`View ${atm.name} in ATM Analytics`}
                                  >
                                    {/* Left: ATM Name */}
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded shrink-0 ${
                                        idx === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                                        darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
                                      }`}>
                                        #{idx + 1}
                                      </span>
                                      <span className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {atm.name}
                                      </span>
                                    </div>
                                    
                                    {/* Center: Top Confluences */}
                                    <div className="flex justify-center">
                                      {atmConfluences.length > 0 && (
                                        <div className="flex items-center gap-1">
                                          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Top:</span>
                                          {atmConfluences.map((conf, i) => (
                                            <span key={conf.id} className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                              {conf.name} <span className={conf.winRate >= 50 ? 'text-green-500' : 'text-red-500'}>({conf.winRate.toFixed(0)}%)</span>
                                              {i < atmConfluences.length - 1 && <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> • </span>}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Right: Stats */}
                                    <div className="flex items-center justify-end gap-4 text-sm">
                                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                                        {atm.trades} trades
                                      </span>
                                      <span className={atm.winRate >= 50 ? 'text-green-500' : 'text-red-500'}>
                                        {atm.winRate.toFixed(0)}% WR
                                      </span>
                                      <span className={`font-semibold ${atm.totalPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                        {atm.totalPnL >= 0 ? '+' : '-'}${fmtUSD(atm.totalPnL)}
                                      </span>
                                    </div>
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          
                          {/* Confluence Performance with this Setup */}
                          {(() => {
                            const confPerf = getConfluencePerformanceForSetup(setup.name);
                            const comboPerf = getComboPerformanceForSetup(setup.name);
                            const hasIndividualData = confPerf.recommended.length > 0 || confPerf.topNonRecommended.length > 0;
                            const hasComboData = comboPerf.autoDiscovered.length > 0 || comboPerf.saved.length > 0;
                            const isCreatingCombo = creatingComboForSetup === setup.name;

                            return (hasIndividualData || hasComboData) ? (
                              <div className="mt-4">
                                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  <Layers size={16} className="text-purple-500" />
                                  Confluence Performance with {setup.name}
                                </h4>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                  {/* LEFT COLUMN: Individual Confluences */}
                                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                                    <div className={`text-xs font-semibold mb-3 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Individual
                                    </div>
                                    {/* Recommended Confluences Performance */}
                                    {confPerf.recommended.length > 0 && (
                                      <div className="mb-4">
                                        <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          Recommended:
                                        </div>
                                        <div className="space-y-1">
                                          {confPerf.recommended.map(conf => (
                                            <div key={conf.id} className="flex items-center justify-between">
                                              <div className="flex items-center gap-2">
                                                <span className="text-yellow-500">★</span>
                                                <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{conf.name}</span>
                                              </div>
                                              <div className="flex items-center gap-3 text-sm">
                                                <span className={`font-semibold ${conf.winRate >= 55 ? 'text-green-500' : conf.winRate >= 45 ? 'text-yellow-500' : 'text-red-500'}`}>
                                                  {conf.winRate.toFixed(0)}% {conf.winRate >= 55 ? '✓' : conf.winRate < 45 ? '⚠' : ''}
                                                </span>
                                                <span className={conf.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}>
                                                  {conf.avgR >= 0 ? '+' : ''}{conf.avgR.toFixed(2)}R
                                                </span>
                                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>({conf.trades} trades)</span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Top Performers (Non-Recommended) — each row carries
                                        a one-click "Pin" icon that adds the confluence to the
                                        setup's recommended list. The Coach already surfaces
                                        observations with structured pin-actions, but having
                                        the inline icon here lets the trader curate directly
                                        from the analytics they're already reading. */}
                                    {confPerf.topNonRecommended.length > 0 && (
                                      <div className="mb-4">
                                        <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          Top Performers (non-recommended):
                                        </div>
                                        <div className="space-y-1">
                                          {confPerf.topNonRecommended.map(conf => (
                                            <div key={conf.id} className="flex items-center justify-between group/conf">
                                              <div className="flex items-center gap-2">
                                                <span className="text-orange-500">🔥</span>
                                                <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{conf.name}</span>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const ok = window.confirm(`Pin "${conf.name}" as a recommended confluence on "${setup.name}"?`);
                                                    if (!ok) return;
                                                    toggleSetupConfluence(setup.name, conf.id, setup.recommendedConfluences || []);
                                                  }}
                                                  title={`Pin "${conf.name}" to this setup's recommended list`}
                                                  className={`
                                                    p-1 rounded-md opacity-60 hover:opacity-100 transition-opacity
                                                    ${darkMode
                                                      ? 'text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10'
                                                      : 'text-cyan-600 hover:text-cyan-700 hover:bg-cyan-100'}
                                                  `}
                                                  aria-label={`Pin ${conf.name} to ${setup.name}`}
                                                >
                                                  <Pin size={12} />
                                                </button>
                                              </div>
                                              <div className="flex items-center gap-3 text-sm">
                                                <span className="font-semibold text-green-500">{conf.winRate.toFixed(0)}%</span>
                                                <span className={conf.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}>
                                                  {conf.avgR >= 0 ? '+' : ''}{conf.avgR.toFixed(2)}R
                                                </span>
                                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>({conf.trades} trades)</span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Underperformers */}
                                    {confPerf.underperformers.length > 0 && (
                                      <div>
                                        <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          Underperformers:
                                        </div>
                                        <div className="space-y-1">
                                          {confPerf.underperformers.map(conf => (
                                            <div key={conf.id} className="flex items-center justify-between">
                                              <div className="flex items-center gap-2">
                                                <span className="text-red-500">⚠</span>
                                                <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{conf.name}</span>
                                              </div>
                                              <div className="flex items-center gap-3 text-sm">
                                                <span className="font-semibold text-red-500">{conf.winRate.toFixed(0)}%</span>
                                                <span className={conf.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}>
                                                  {conf.avgR >= 0 ? '+' : ''}{conf.avgR.toFixed(2)}R
                                                </span>
                                                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>({conf.trades} trades)</span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {!hasIndividualData && (
                                      <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        No individual confluence data yet
                                      </p>
                                    )}
                                  </div>

                                  {/* RIGHT COLUMN: Combinations */}
                                  <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                                    <div className="flex items-center justify-between mb-3">
                                      <div className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <Link2 size={12} />
                                        Combinations
                                      </div>
                                      <button
                                        onClick={() => {
                                          if (isCreatingCombo) {
                                            setCreatingComboForSetup(null);
                                            setNewComboName('');
                                            setNewComboIds([]);
                                          } else {
                                            setCreatingComboForSetup(setup.name);
                                            setNewComboName('');
                                            setNewComboIds([]);
                                          }
                                        }}
                                        className={`p-1 rounded transition-colors ${
                                          isCreatingCombo
                                            ? 'text-red-400 hover:bg-red-500/20'
                                            : darkMode ? 'text-gray-400 hover:text-purple-400 hover:bg-gray-600/50' : 'text-gray-400 hover:text-purple-500 hover:bg-gray-200'
                                        }`}
                                        title={isCreatingCombo ? 'Cancel' : 'Create combo'}
                                      >
                                        {isCreatingCombo ? <X size={14} /> : <Plus size={14} />}
                                      </button>
                                    </div>

                                    {/* Inline Create Combo Form */}
                                    {isCreatingCombo && (
                                      <div className={`mb-4 p-3 rounded-lg border ${darkMode ? 'bg-gray-800/60 border-purple-500/30' : 'bg-white border-purple-300'}`}>
                                        <input
                                          type="text"
                                          value={newComboName}
                                          onChange={(e) => setNewComboName(e.target.value)}
                                          placeholder="e.g. EMA Stack + Key Level"
                                          className="w-full input-modern mb-2"
                                        />
                                        <div className={`text-xs font-medium mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          Select 2-3 confluences:
                                        </div>
                                        <div className="max-h-36 overflow-y-auto space-y-0.5 mb-2">
                                          {confluences.map(cf => {
                                            const isSelected = newComboIds.includes(cf.id);
                                            const isDisabled = !isSelected && newComboIds.length >= 3;
                                            return (
                                              <label
                                                key={cf.id}
                                                className={`flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer transition-colors ${
                                                  isDisabled ? 'opacity-40 cursor-not-allowed' :
                                                  isSelected
                                                    ? darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-50 text-purple-700'
                                                    : darkMode ? 'hover:bg-gray-700/50 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                                                }`}
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={isSelected}
                                                  disabled={isDisabled}
                                                  onChange={() => {
                                                    if (isSelected) {
                                                      setNewComboIds(prev => prev.filter(id => id !== cf.id));
                                                    } else if (newComboIds.length < 3) {
                                                      setNewComboIds(prev => [...prev, cf.id]);
                                                    }
                                                  }}
                                                  className="accent-purple-500 rounded-sm"
                                                />
                                                <span className="truncate">{cf.name}</span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <button
                                            onClick={() => {
                                              if (newComboIds.length >= 2 && newComboName.trim()) {
                                                const newCombo = {
                                                  id: `combo_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                                                  name: newComboName.trim(),
                                                  confluenceIds: [...newComboIds],
                                                  createdAt: new Date().toISOString()
                                                };
                                                setConfluenceCombos(prev => [...prev, newCombo]);
                                                setCreatingComboForSetup(null);
                                                setNewComboName('');
                                                setNewComboIds([]);
                                              }
                                            }}
                                            disabled={newComboIds.length < 2 || !newComboName.trim()}
                                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                              newComboIds.length >= 2 && newComboName.trim()
                                                ? 'bg-purple-600 hover:bg-purple-500 text-white'
                                                : darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            }`}
                                          >
                                            Save Combo
                                          </button>
                                          <button
                                            onClick={() => {
                                              setCreatingComboForSetup(null);
                                              setNewComboName('');
                                              setNewComboIds([]);
                                            }}
                                            className={`px-3 py-1 rounded-sm text-xs font-medium ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                                          >
                                            Cancel
                                          </button>
                                          {newComboIds.length > 0 && (
                                            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                              {newComboIds.length}/3 selected
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Saved Combos (pinned) */}
                                    {comboPerf.saved.length > 0 && (
                                      <div className="space-y-2 mb-3">
                                        {comboPerf.saved.map(combo => (
                                          <div key={combo.key} className={`group p-2.5 rounded-lg ${darkMode ? 'bg-gray-800/40' : 'bg-white'}`}>
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5 mb-1">
                                                  <span className="text-sm">📌</span>
                                                  <span className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {combo.comboName}
                                                  </span>
                                                </div>
                                                <div className={`text-xs mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                  {combo.names.join(' + ')}
                                                </div>
                                                <div className="flex items-center gap-2 text-xs">
                                                  <span className={`font-semibold ${combo.winRate >= 55 ? 'text-green-500' : combo.winRate >= 45 ? 'text-yellow-500' : 'text-red-500'}`}>
                                                    {combo.trades > 0 ? `${combo.winRate.toFixed(0)}% WR` : 'No data'}
                                                  </span>
                                                  {combo.trades > 0 && (
                                                    <>
                                                      <span className={darkMode ? 'text-gray-600' : 'text-gray-300'}>·</span>
                                                      <span className={combo.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}>
                                                        {combo.avgR >= 0 ? '+' : ''}{combo.avgR.toFixed(2)}R
                                                      </span>
                                                      <span className={darkMode ? 'text-gray-600' : 'text-gray-300'}>·</span>
                                                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{combo.trades} trades</span>
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                              <button
                                                onClick={() => setConfluenceCombos(prev => prev.filter(c => c.id !== combo.comboId))}
                                                className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${
                                                  darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                                                }`}
                                                title="Remove combo"
                                              >
                                                <X size={12} />
                                              </button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Auto-Discovered Combos */}
                                    {comboPerf.autoDiscovered.length > 0 && (
                                      <div>
                                        {comboPerf.saved.length > 0 && (
                                          <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                            Auto-discovered:
                                          </div>
                                        )}
                                        <div className="space-y-2">
                                          {comboPerf.autoDiscovered.map((combo, idx) => {
                                            const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '•';
                                            return (
                                              <div key={combo.key} className={`p-2.5 rounded-lg ${darkMode ? 'bg-gray-800/40' : 'bg-white'}`}>
                                                <div className="flex items-center gap-1.5 mb-1">
                                                  <span className={`text-sm ${idx > 2 ? (darkMode ? 'text-gray-500' : 'text-gray-400') : ''}`}>{medal}</span>
                                                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                                    {combo.names.join(' + ')}
                                                  </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs">
                                                  <span className={`font-semibold ${combo.winRate >= 55 ? 'text-green-500' : combo.winRate >= 45 ? 'text-yellow-500' : 'text-red-500'}`}>
                                                    {combo.winRate.toFixed(0)}% WR
                                                  </span>
                                                  <span className={darkMode ? 'text-gray-600' : 'text-gray-300'}>·</span>
                                                  <span className={combo.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}>
                                                    {combo.avgR >= 0 ? '+' : ''}{combo.avgR.toFixed(2)}R
                                                  </span>
                                                  <span className={darkMode ? 'text-gray-600' : 'text-gray-300'}>·</span>
                                                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{combo.trades} trades</span>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    {/* Empty state */}
                                    {!hasComboData && !isCreatingCombo && (
                                      <p className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        Not enough data for combo analysis (need trades with 2+ confluences)
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : null;
                          })()}

                          {/* R-Distribution */}
                          {(() => {
                            const rDist = getRDistributionForTrades(setupTrades);
                            if (rDist.withR < 3) return null;
                            return (
                              <div className="mt-4">
                                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  <BarChart3 size={16} className="text-cyan-500" />
                                  R-Multiple Distribution
                                </h4>
                                <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                                  <div className="space-y-2">
                                    {rDist.data.map(d => (
                                      <div key={d.threshold} className="flex items-center gap-3">
                                        <span className={`text-xs font-mono w-8 text-right ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{d.threshold}</span>
                                        <div className={`flex-1 h-6 rounded-full overflow-hidden ${darkMode ? 'bg-gray-600/50' : 'bg-gray-200'}`}>
                                          <div
                                            className={`h-full rounded-full transition-all duration-500 ${
                                              d.threshold === '≥1R' ? 'bg-green-400' :
                                              d.threshold === '≥2R' ? 'bg-green-500' :
                                              d.threshold === '≥3R' ? 'bg-emerald-500' : 'bg-emerald-600'
                                            }`}
                                            style={{ width: `${Math.max(d.pct, 2)}%` }}
                                          />
                                        </div>
                                        <span className={`text-xs font-semibold w-20 text-right ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                          {d.pct.toFixed(0)}% <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>({d.count})</span>
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                  <div className={`mt-3 pt-3 border-t flex flex-wrap gap-4 text-xs ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'}`}>
                                    <span>Avg Winner: <span className="text-green-500 font-semibold">+{rDist.avgWinR.toFixed(2)}R</span></span>
                                    <span>Avg Loser: <span className="text-red-500 font-semibold">-{rDist.avgLossR.toFixed(2)}R</span></span>
                                    <span>Edge: <span className={`font-semibold ${rDist.edgeScore >= 0 ? 'text-green-500' : 'text-red-500'}`}>{rDist.edgeScore >= 0 ? '+' : ''}{rDist.edgeScore.toFixed(2)}</span></span>
                                    <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>({rDist.withR} trades with R data)</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Stop Loss Breakdown */}
                          {(() => {
                            const slBreakdown = getStopLossBreakdownForSetup(setup.name);
                            if (slBreakdown.length < 2) return null;
                            return (
                              <div className="mt-4">
                                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  <Target size={16} className="text-amber-500" />
                                  Stop Loss Breakdown
                                </h4>
                                <div className="grid gap-2">
                                  {slBreakdown.map((sl, idx) => (
                                    <div
                                      key={sl.slSize}
                                      className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-white'} ${idx === 0 ? (darkMode ? 'ring-1 ring-amber-500/40' : 'ring-1 ring-amber-400/50') : ''}`}
                                    >
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <span className={`text-xs font-bold px-1.5 py-0.5 rounded shrink-0 ${
                                            idx === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                                            darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
                                          }`}>
                                            #{idx + 1}
                                          </span>
                                          <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                            {sl.slSize}p SL
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs">
                                          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{sl.trades} trades</span>
                                          <span className={`font-semibold ${sl.winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>{sl.winRate.toFixed(0)}% WR</span>
                                          <span className={`font-semibold ${sl.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>{sl.avgR >= 0 ? '+' : ''}{sl.avgR.toFixed(2)}R</span>
                                          <span className={`px-1.5 py-0.5 rounded-sm text-xs font-bold ${sl.edgeScore >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                            Edge {sl.edgeScore >= 0 ? '+' : ''}{sl.edgeScore.toFixed(2)}
                                          </span>
                                        </div>
                                      </div>
                                      {sl.rDist.withR >= 2 && (
                                        <div className="flex gap-2">
                                          {sl.rDist.data.map(d => (
                                            <span key={d.threshold} className={`text-xs px-2 py-0.5 rounded ${
                                              d.pct >= 50 ? (darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700') :
                                              d.pct >= 25 ? (darkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700') :
                                              darkMode ? 'bg-gray-600/50 text-gray-400' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                              {d.threshold}: {d.pct.toFixed(0)}%
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Direction Split */}
                          {(() => {
                            const dirSplit = getDirectionSplitForSetup(setup.name);
                            if (dirSplit.long.trades < 2 && dirSplit.short.trades < 2) return null;
                            const strongerDir = dirSplit.long.edgeScore >= dirSplit.short.edgeScore ? 'long' : 'short';
                            const renderSide = (label, stats, color, icon) => (
                              <div className={`flex-1 p-3 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-white'} ${
                                stats.trades >= 2 && ((label === 'Long' && strongerDir === 'long') || (label === 'Short' && strongerDir === 'short'))
                                  ? (darkMode ? `ring-1 ring-${color}-500/40` : `ring-1 ring-${color}-400/50`)
                                  : ''
                              }`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`text-${color}-500`}>{icon}</span>
                                  <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{label}</span>
                                  {stats.trades >= 2 && ((label === 'Long' && strongerDir === 'long') || (label === 'Short' && strongerDir === 'short')) && (
                                    <span className={`text-xs px-1.5 py-0.5 rounded-sm bg-${color}-500/20 text-${color}-400`}>Stronger</span>
                                  )}
                                </div>
                                {stats.trades < 2 ? (
                                  <p className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Not enough data</p>
                                ) : (
                                  <>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
                                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Trades</span>
                                      <span className={`font-semibold text-right ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{stats.trades}</span>
                                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Win Rate</span>
                                      <span className={`font-semibold text-right ${stats.winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>{stats.winRate.toFixed(0)}%</span>
                                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Avg R</span>
                                      <span className={`font-semibold text-right ${stats.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>{stats.avgR >= 0 ? '+' : ''}{stats.avgR.toFixed(2)}R</span>
                                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Edge</span>
                                      <span className={`font-semibold text-right ${stats.edgeScore >= 0 ? 'text-green-500' : 'text-red-500'}`}>{stats.edgeScore >= 0 ? '+' : ''}{stats.edgeScore.toFixed(2)}</span>
                                    </div>
                                    {stats.rDist.withR >= 2 && (
                                      <div className="flex flex-wrap gap-1">
                                        {stats.rDist.data.map(d => (
                                          <span key={d.threshold} className={`text-xs px-1.5 py-0.5 rounded ${
                                            d.pct >= 50 ? (darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700') :
                                            d.pct >= 25 ? (darkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700') :
                                            darkMode ? 'bg-gray-600/50 text-gray-400' : 'bg-gray-100 text-gray-500'
                                          }`}>
                                            {d.threshold}: {d.pct.toFixed(0)}%
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                            return (
                              <div className="mt-4">
                                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  <ArrowUpDown size={16} className="text-blue-500" />
                                  Direction Split
                                </h4>
                                <div className="grid grid-cols-2 gap-3">
                                  {renderSide('Long', dirSplit.long, 'blue', '↑')}
                                  {renderSide('Short', dirSplit.short, 'red', '↓')}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Counter-Confluence Impact */}
                          {(() => {
                            const ccImpact = getCounterConfluenceImpactForSetup(setup.name);
                            if (!ccImpact.withCC || ccImpact.withCC.trades < 3) return null;
                            return (
                              <div className="mt-4">
                                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  <Zap size={16} className="text-orange-500" />
                                  Counter-Confluence Impact
                                </h4>
                                <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                                  {/* Comparison */}
                                  <div className="grid grid-cols-2 gap-4 mb-3">
                                    <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-orange-900/20 border border-orange-500/20' : 'bg-orange-50 border border-orange-200'}`}>
                                      <div className={`text-xs mb-1 ${darkMode ? 'text-orange-300' : 'text-orange-600'}`}>With Against-Signals</div>
                                      <div className={`text-lg font-bold ${ccImpact.withCC.winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                                        {ccImpact.withCC.winRate.toFixed(0)}% WR
                                      </div>
                                      <div className={`text-xs ${ccImpact.withCC.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                                        {ccImpact.withCC.avgR >= 0 ? '+' : ''}{ccImpact.withCC.avgR.toFixed(2)}R avg
                                      </div>
                                      <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>({ccImpact.withCC.trades} trades)</div>
                                    </div>
                                    {ccImpact.withoutCC && (
                                      <div className={`p-3 rounded-lg text-center ${darkMode ? 'bg-gray-600/30 border border-gray-500/20' : 'bg-gray-100 border border-gray-200'}`}>
                                        <div className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Without Against-Signals</div>
                                        <div className={`text-lg font-bold ${ccImpact.withoutCC.winRate >= 50 ? 'text-green-500' : 'text-red-500'}`}>
                                          {ccImpact.withoutCC.winRate.toFixed(0)}% WR
                                        </div>
                                        <div className={`text-xs ${ccImpact.withoutCC.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                                          {ccImpact.withoutCC.avgR >= 0 ? '+' : ''}{ccImpact.withoutCC.avgR.toFixed(2)}R avg
                                        </div>
                                        <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>({ccImpact.withoutCC.trades} trades)</div>
                                      </div>
                                    )}
                                  </div>
                                  {/* Delta */}
                                  {ccImpact.delta && (
                                    <div className={`text-center text-xs py-2 rounded mb-3 ${
                                      ccImpact.delta.wrDelta < 0
                                        ? (darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-600')
                                        : (darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-600')
                                    }`}>
                                      {ccImpact.delta.wrDelta < 0 ? '↓' : '↑'} {Math.abs(ccImpact.delta.wrDelta).toFixed(0)}% WR when trading against signals
                                      {' • '}
                                      {ccImpact.delta.rDelta < 0 ? '↓' : '↑'} {Math.abs(ccImpact.delta.rDelta).toFixed(2)}R avg
                                    </div>
                                  )}
                                  {/* Individual counter-confluences */}
                                  {ccImpact.individual.length > 0 && (
                                    <div>
                                      <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Most Predictive Against-Signals:
                                      </div>
                                      <div className="space-y-1">
                                        {ccImpact.individual.slice(0, 5).map(cc => (
                                          <div key={cc.id} className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <span className="text-orange-500">✗</span>
                                              <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{cc.name}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs">
                                              <span className={`font-semibold ${cc.winRate < 45 ? 'text-red-500' : cc.winRate >= 55 ? 'text-green-500' : 'text-yellow-500'}`}>
                                                {cc.winRate.toFixed(0)}% WR
                                              </span>
                                              <span className={`${cc.avgR >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                                                {cc.avgR >= 0 ? '+' : ''}{cc.avgR.toFixed(2)}R
                                              </span>
                                              <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>({cc.trades})</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                        </div>
                      )}
                    </div>
                  );
                  });
                })()}
              </div>
                )}
              </>
            )}
          </div>
  );
};

export default SetupsTab;
